import { create } from 'zustand';
import { registerUserScopedReset, withSession } from './session-read';
import api from '../services/api';
import { useAuthStore } from './auth.store';
import { useGuestStore } from './guest.store';
import type { DomainWithPaths, LessonWithProgress, PathWithProgress } from '../types';
import { safeGetItem, safeSetItem } from '../utils/storage';
import { isLessonInProgress, isLessonUpNext } from './next-lesson';

/**
 * Guest-mode lesson decoration: the public preview endpoint serves intro lessons
 * with fresh-user flags (nothing locked), and THIS mirrors the server's per-lesson
 * progression (PathController::lessons) against the guest's LOCAL results:
 * first lesson open; each next lesson locked until the previous applicable
 * (non-premium) quiz is passed locally. Pass is sticky (see guest.store), matching
 * the server's `completed = completed || passed`.
 */
function decorateGuestLessons(lessons: LessonWithProgress[]): LessonWithProgress[] {
  const results = useGuestStore.getState().quizResults;
  const passedLocally = (quizId: number | undefined): boolean => {
    if (!quizId) return false;
    const r = results[quizId];
    if (!r) return false;
    // Older stored results lack `passed` — fall back to the 70% default threshold.
    return r.passed ?? r.score / Math.max(r.total, 1) >= 0.7;
  };

  let previousQuizId: number | undefined;
  return lessons.map((lesson) => {
    const quiz = lesson.quiz as { id: number; premium_only?: boolean } | undefined;
    const locked = previousQuizId !== undefined && !passedLocally(previousQuizId);
    const tried = quiz ? results[quiz.id] !== undefined : false;

    // A premium-only quiz is never "applicable" for a free/guest user — it can't
    // gate the next lesson (same skip the server does).
    if (quiz && !quiz.premium_only) {
      previousQuizId = quiz.id;
    }

    return {
      ...lesson,
      locked,
      passed: quiz ? passedLocally(quiz.id) : false,
      never_tried: !tried,
    };
  });
}

// ============================================
// Constants
// ============================================

const STORAGE_KEY_CURRENT_PATH = 'empathease:currentPathId';
const STORAGE_KEY_MOBILE_FILTER = 'empathease:mobileFilter';
const STORAGE_KEY_SIDEBAR_COLLAPSED = 'empathease:sidebarCollapsed';

// ============================================
// Type Definitions
// ============================================

export type MobileFilterMode = 'full' | 'domain' | 'path' | 'lesson';

export interface CourseContext {
  domainId: number | null;
  pathId: number | null;
  lessonId: number | null;
}

interface DomainState {
  domains: DomainWithPaths[];
  isLoading: boolean;
  expandedDomains: Record<number, boolean>;
  expandedPaths: Record<number, boolean>;
  pathLessons: Record<number, LessonWithProgress[]>;
  loadingPaths: Record<number, boolean>;
  errorPaths: Record<number, boolean>;
  abortControllers: Record<number, AbortController>;
  // Navigation state
  sidebarCollapsed: boolean;
  mobileFilter: MobileFilterMode;
  currentContext: CourseContext | null;
}

interface DomainActions {
  setDomains: (domains: DomainWithPaths[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  toggleDomain: (domainId: number) => void;
  togglePath: (pathId: number) => void;
  fetchDomains: (silent?: boolean) => Promise<void>;
  fetchLessonsForPath: (pathId: number, force?: boolean) => Promise<void>;
  retryFetchLessonsForPath: (pathId: number) => Promise<void>;
  expandFirstDomainAndPath: () => void;
  expandToPath: (pathId: number) => void;
  findTargetLessonPath: () => { pathId: number; lessonId: number } | null;
  // Cache invalidation
  invalidateCache: () => void;
  /** HARD user-data reset (logout / entering guest mode): unlike invalidateCache,
   *  this also drops expanded paths' cached lessons — they carry per-user fields
   *  (best_attempt/passed/locks) that must never leak across identities. */
  resetUserData: () => void;
  refreshDomains: () => Promise<void>;
  // Navigation actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileFilter: (filter: MobileFilterMode) => void;
  setCurrentContext: (context: CourseContext | null) => void;
  initializeContext: () => void;
  navigateToSection: (domainId: number, pathId?: number) => void;
}

type DomainStore = DomainState & DomainActions;

// ============================================
// Store Implementation
// ============================================

// Load persisted values from localStorage
const getStoredMobileFilter = (): MobileFilterMode => {
  const stored = safeGetItem(STORAGE_KEY_MOBILE_FILTER);
  if (stored && ['full', 'domain', 'path', 'lesson'].includes(stored)) {
    return stored as MobileFilterMode;
  }
  return 'full';
};

const getStoredSidebarCollapsed = (): boolean => {
  const stored = safeGetItem(STORAGE_KEY_SIDEBAR_COLLAPSED);
  return stored === 'true'; // Default to false (expanded)
};

export const useDomainStore = create<DomainStore>((set, get) => ({
  domains: [],
  isLoading: false,
  expandedDomains: {},
  expandedPaths: {},
  pathLessons: {},
  loadingPaths: {},
  errorPaths: {},
  abortControllers: {},
  // Navigation state (persisted)
  sidebarCollapsed: getStoredSidebarCollapsed(),
  mobileFilter: getStoredMobileFilter(),
  currentContext: null,

  setDomains: (domains: DomainWithPaths[]) => set({ domains }),
  setIsLoading: (isLoading: boolean) => set({ isLoading }),

  toggleDomain: (domainId: number) => {
    const { expandedDomains } = get();
    set({
      expandedDomains: {
        ...expandedDomains,
        [domainId]: !expandedDomains[domainId],
      },
    });
  },

  togglePath: (pathId: number) => {
    const { expandedPaths, pathLessons } = get();
    const isCurrentlyExpanded = expandedPaths[pathId];

    // If expanding, save as current path and fetch lessons if needed
    if (!isCurrentlyExpanded) {
      safeSetItem(STORAGE_KEY_CURRENT_PATH, String(pathId));
      if (!pathLessons[pathId]) {
        void get().fetchLessonsForPath(pathId);
      }
    }

    set({
      expandedPaths: {
        ...expandedPaths,
        [pathId]: !isCurrentlyExpanded,
      },
    });
  },

  fetchLessonsForPath: async (pathId: number, force = false) => {
    const { loadingPaths, pathLessons, abortControllers } = get();

    // Skip if already loading, or already have lessons (unless forced)
    if (loadingPaths[pathId] || (!force && pathLessons[pathId])) {
      return;
    }

    // Cancel any existing request for this path
    if (abortControllers[pathId]) {
      abortControllers[pathId].abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    set({
      loadingPaths: { ...get().loadingPaths, [pathId]: true },
      errorPaths: { ...get().errorPaths, [pathId]: false },
      abortControllers: { ...get().abortControllers, [pathId]: controller },
    });

    const isGuest = useAuthStore.getState().isGuest;
    const lessonsUrl = isGuest
      ? `/preview/lessons/path/${pathId}` // intro-only; same envelope as the authed endpoint
      : `/lessons/path/${pathId}`;
    // Session-bound (backlog c-030 sweep): an answer for the previous account
    // writes nothing, not even the loading cleanup, which would otherwise end
    // the next account's own load of the same path.
    const outcome = await withSession(() => api.get(lessonsUrl, { signal: controller.signal }));
    if (outcome.status === 'ended') return;

    try {
      if (outcome.status === 'failed') throw outcome.error;
      const { data, error } = outcome.value.data;

      if (error) {
        console.error('Error fetching lessons for path:', error);
        set({
          errorPaths: { ...get().errorPaths, [pathId]: true },
        });
      } else {
        const lessons = (data.lessons || []) as LessonWithProgress[];
        set({
          pathLessons: {
            ...get().pathLessons,
            // Guests: apply the same sequential unlock a real user gets, computed
            // from their local quiz results (the server can't know them).
            [pathId]: isGuest ? decorateGuestLessons(lessons) : lessons,
          },
          errorPaths: { ...get().errorPaths, [pathId]: false },
        });
      }
    } catch (error) {
      // Don't set error state if request was cancelled
      const isCancelled =
        error && typeof error === 'object' && 'code' in error && error.code === 'ERR_CANCELED';
      if (!isCancelled) {
        console.error('Error fetching lessons for path:', error);
        set({
          errorPaths: { ...get().errorPaths, [pathId]: true },
        });
      }
    }

    // Clean up abort controller
    const { abortControllers: currentControllers } = get();
    const { [pathId]: _, ...remainingControllers } = currentControllers;
    set({
      loadingPaths: {
        ...get().loadingPaths,
        [pathId]: false,
      },
      abortControllers: remainingControllers,
    });
  },

  retryFetchLessonsForPath: async (pathId: number) => {
    // Clear cached lessons and error state to force a refetch
    const { pathLessons, errorPaths } = get();
    const { [pathId]: _, ...remainingLessons } = pathLessons;
    set({
      pathLessons: remainingLessons,
      errorPaths: { ...errorPaths, [pathId]: false },
    });

    // Fetch again
    await get().fetchLessonsForPath(pathId);
  },

  expandFirstDomainAndPath: () => {
    const { domains, expandedDomains, expandedPaths } = get();

    // If there are domains and none are expanded yet
    if (domains.length > 0 && Object.keys(expandedDomains).length === 0) {
      // Check if user has a previously selected path stored
      const storedPathId = safeGetItem(STORAGE_KEY_CURRENT_PATH);
      let targetDomain: DomainWithPaths | null = null;
      let targetPath: PathWithProgress | null = null;

      if (storedPathId) {
        const pathId = parseInt(storedPathId);
        // Find the domain containing this path
        for (const domain of domains) {
          const foundPath = domain.paths?.find((p) => p.id === pathId);
          if (foundPath && !foundPath.locked) {
            targetDomain = domain;
            targetPath = foundPath;
            break;
          }
        }
      }

      // If no stored path or stored path not found, find the first in-progress path
      if (!targetPath) {
        for (const domain of domains) {
          // Find first path that's unlocked and not mastered (in progress)
          const inProgressPath = domain.paths?.find(
            (p) => !p.locked && !p.mastered && (p.progress || 0) > 0,
          );
          if (inProgressPath) {
            targetDomain = domain;
            targetPath = inProgressPath;
            break;
          }
        }
      }

      // If no in-progress path, find first path that's unlocked and never started
      if (!targetPath) {
        for (const domain of domains) {
          const unlockedPath = domain.paths?.find((p) => !p.locked && !p.mastered);
          if (unlockedPath) {
            targetDomain = domain;
            targetPath = unlockedPath;
            break;
          }
        }
      }

      // Fallback: just use first domain and first unlocked path
      if (!targetPath && domains.length > 0) {
        targetDomain = domains[0];
        targetPath = targetDomain.paths?.find((p) => !p.locked) || null;
      }

      // Apply the expansion
      if (targetDomain) {
        const newExpandedDomains = { [targetDomain.id]: true };
        let newExpandedPaths = { ...expandedPaths };

        if (targetPath) {
          newExpandedPaths = { [targetPath.id]: true };
          void get().fetchLessonsForPath(targetPath.id);
        }

        set({
          expandedDomains: newExpandedDomains,
          expandedPaths: newExpandedPaths,
        });
      }
    }
  },

  fetchDomains: async (silent = false) => {
    if (!silent) {
      set({ isLoading: true });
    }

    // Guests get the SAME payload shape from the public preview endpoint, computed
    // for an anonymous fresh user (full tree, only the intro path unlocked) — so
    // the real Course page renders identically, just limited.
    const isGuest = useAuthStore.getState().isGuest;
    // Session-bound (backlog c-030): A's answer landing after a switch to B
    // writes nothing, neither A's lock/progress tree nor the loading flag B's
    // own fetch may still be holding.
    const outcome = await withSession(() => api.get(isGuest ? '/preview/domains' : '/domains'));
    if (outcome.status === 'ended') return;

    try {
      if (outcome.status === 'failed') throw outcome.error;
      const data = outcome.value.data;
      if (!data.success) {
        console.error('Failed to fetch domains:', data);
      } else {
        set({ domains: data.domains });

        // Stale-while-revalidate for lesson data:
        // 1. Clear cached lessons for collapsed paths (refetch on next expand)
        // 2. Keep expanded paths' data visible (no blank flash)
        // 3. Force-refetch expanded paths in the background (fresh lock/progress)
        const expandedPaths = get().expandedPaths;
        const expandedPathIds = Object.keys(expandedPaths)
          .filter((id) => expandedPaths[Number(id)])
          .map(Number);

        // Clear collapsed paths' cache
        const kept: Record<number, LessonWithProgress[]> = {};
        for (const [pathIdStr, lessons] of Object.entries(get().pathLessons)) {
          if (expandedPaths[Number(pathIdStr)]) {
            kept[Number(pathIdStr)] = lessons;
          }
        }
        set({ pathLessons: kept });

        // Background refetch for expanded paths (stale data visible while loading)
        for (const pathId of expandedPathIds) {
          void get().fetchLessonsForPath(pathId, true);
        }
      }
    } catch (error) {
      console.error('Error fetching domains:', error);
    }

    if (!silent) {
      set({ isLoading: false });
    }
  },

  /**
   * Invalidate the domain cache so next fetch gets fresh data.
   * Call this after quiz completion to ensure lock status is updated.
   * Preserves UI state (expanded domains/paths) but clears data caches.
   */
  invalidateCache: () => {
    // Clear domain data but keep lesson data for expanded paths
    // to avoid "No lessons available" flash (stale-while-revalidate)
    const expandedPaths = get().expandedPaths;
    const kept: Record<number, LessonWithProgress[]> = {};
    for (const [pathIdStr, lessons] of Object.entries(get().pathLessons)) {
      if (expandedPaths[Number(pathIdStr)]) {
        kept[Number(pathIdStr)] = lessons;
      }
    }
    set({
      domains: [],
      pathLessons: kept,
      // Keep expandedDomains, expandedPaths - these are UI state, not data cache
      // Keep currentContext, mobileFilter, sidebarCollapsed - these are user preferences
    });
  },

  resetUserData: () => {
    // Abort any in-flight lesson fetches so a late response can't repopulate the
    // old identity's data after the clear.
    for (const controller of Object.values(get().abortControllers)) {
      controller.abort();
    }
    set({
      domains: [],
      // A fetch for the previous account that was out writes nothing when it
      // lands (session-bound), so it cannot clear its own loading flag.
      isLoading: false,
      pathLessons: {},
      loadingPaths: {},
      errorPaths: {},
      abortControllers: {},
      // expanded* / preferences remain — pure UI state, no user data.
    });
  },

  /**
   * Refresh domains by invalidating cache and fetching fresh data.
   * Use this when returning to Course page after quiz completion.
   */
  refreshDomains: async () => {
    get().invalidateCache();
    await get().fetchDomains();
  },

  expandToPath: (pathId: number) => {
    const { domains, expandedDomains, expandedPaths, pathLessons } = get();

    // Find the domain containing this path
    let targetDomain: DomainWithPaths | null = null;
    for (const domain of domains) {
      if (domain.paths?.some((p) => p.id === pathId)) {
        targetDomain = domain;
        break;
      }
    }

    if (!targetDomain) return;

    // Expand the domain if not already
    const newExpandedDomains = { ...expandedDomains };
    if (!newExpandedDomains[targetDomain.id]) {
      newExpandedDomains[targetDomain.id] = true;
    }

    // Expand the path if not already
    const newExpandedPaths = { ...expandedPaths };
    if (!newExpandedPaths[pathId]) {
      newExpandedPaths[pathId] = true;
    }

    set({
      expandedDomains: newExpandedDomains,
      expandedPaths: newExpandedPaths,
    });

    // Fetch lessons if not already loaded
    if (!pathLessons[pathId]) {
      void get().fetchLessonsForPath(pathId);
    }
  },

  findTargetLessonPath: () => {
    const { pathLessons } = get();

    // Priority 1: Find in-progress lesson (has best_attempt, not passed)
    for (const [pathIdStr, lessons] of Object.entries(pathLessons)) {
      const pathId = parseInt(pathIdStr);
      const inProgress = lessons.find(isLessonInProgress);
      if (inProgress) {
        return { pathId, lessonId: inProgress.id };
      }
    }

    // Priority 2: Find next available lesson (unlocked, not passed, never tried)
    for (const [pathIdStr, lessons] of Object.entries(pathLessons)) {
      const pathId = parseInt(pathIdStr);
      const next = lessons.find(isLessonUpNext);
      if (next) {
        return { pathId, lessonId: next.id };
      }
    }

    // Priority 3: Find last lesson of the last loaded path (all complete)
    const pathIds = Object.keys(pathLessons)
      .map(Number)
      .sort((a, b) => a - b);
    if (pathIds.length > 0) {
      const lastPathId = pathIds[pathIds.length - 1];
      const lessons = pathLessons[lastPathId];
      if (lessons && lessons.length > 0) {
        return { pathId: lastPathId, lessonId: lessons[lessons.length - 1].id };
      }
    }

    return null;
  },

  // Navigation actions
  toggleSidebar: () => {
    const newValue = !get().sidebarCollapsed;
    safeSetItem(STORAGE_KEY_SIDEBAR_COLLAPSED, String(newValue));
    set({ sidebarCollapsed: newValue });
  },

  setSidebarCollapsed: (collapsed: boolean) => {
    safeSetItem(STORAGE_KEY_SIDEBAR_COLLAPSED, String(collapsed));
    set({ sidebarCollapsed: collapsed });
  },

  setMobileFilter: (filter: MobileFilterMode) => {
    safeSetItem(STORAGE_KEY_MOBILE_FILTER, filter);
    set({ mobileFilter: filter });
  },

  setCurrentContext: (context: CourseContext | null) => {
    set({ currentContext: context });
  },

  initializeContext: () => {
    const { domains, pathLessons } = get();
    const target = get().findTargetLessonPath();

    if (target) {
      // Find domain containing this path
      const domain = domains.find((d) => d.paths?.some((p) => p.id === target.pathId));

      set({
        currentContext: {
          domainId: domain?.id ?? null,
          pathId: target.pathId,
          lessonId: target.lessonId,
        },
      });
    } else if (domains.length > 0) {
      // Fallback: use first domain
      const firstDomain = domains[0];
      const firstPath = firstDomain.paths?.[0];
      const lessons = firstPath ? pathLessons[firstPath.id] : null;

      set({
        currentContext: {
          domainId: firstDomain.id,
          pathId: firstPath?.id ?? null,
          lessonId: lessons?.[0]?.id ?? null,
        },
      });
    }
  },

  navigateToSection: (domainId: number, pathId?: number) => {
    const { expandedDomains, toggleDomain, expandToPath, pathLessons, currentContext } = get();

    // Update current context to show selected domain/path
    const lessons = pathId ? pathLessons[pathId] : null;
    const firstLessonId = lessons?.[0]?.id ?? currentContext?.lessonId ?? null;

    set({
      currentContext: {
        domainId,
        pathId: pathId ?? currentContext?.pathId ?? null,
        lessonId: firstLessonId,
      },
    });

    // If path specified, ensure domain is expanded and expand path
    if (pathId) {
      // Ensure domain is expanded (don't toggle if already expanded)
      if (!expandedDomains[domainId]) {
        toggleDomain(domainId);
      }
      expandToPath(pathId);

      // Small delay for DOM update, then scroll
      setTimeout(() => {
        const pathElement = document.querySelector(`[data-path-id="${pathId}"]`);
        if (pathElement) {
          pathElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      // Toggle domain expansion in sidebar (allows collapsing/expanding)
      toggleDomain(domainId);

      // Scroll to domain
      setTimeout(() => {
        const domainElement = document.querySelector(`[data-domain-id="${domainId}"]`);
        if (domainElement) {
          domainElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  },
}));

// Per-user: reset synchronously on every session change and on entering the guest tour (#940 r9).
// Hard reset, NOT invalidateCache — that one deliberately keeps expanded paths'
// cached lessons, which carry per-user best_attempt/passed/lock fields.
registerUserScopedReset(() => useDomainStore.getState().resetUserData());
